import { useContext, useEffect, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  Backdrop,
  Box,
  Button,
  FormControl,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { BCPXmark } from "../../../../components/icons/bcp/BCPIcons";

import DeleteAccount from "./DeleteAccount";

import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { postNotification } from "../../../../components/utilities/Notifications";
import {
  areObjectsEqual,
  postData
} from "../../../../components/utilities/UtilityFunctions";
import { refreshTokens } from "../../../../components/user/BCPAuth";
import {
  AuthContext,
  currentAuthSession
} from "../../../../components/user/BCPAuthProvider";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import HelpButton from "../../../../components/utilities/HelpButton";
import {
  getCookie,
  getCookieTypes,
  getLocalStorage,
  setLocalStorage
} from "../../../../components/layout/cookies/CookieFunctions";
import { useSetCookieConsent } from "../../../../components/user/BCPAuthReducers";
import { getLanguageCookie } from "../../../../language/LanguageFunctions";
import { useTranslation } from "react-i18next";

const IS_BCP =
  getEnvironmentVariable("IS_BCP") ||
  getEnvironmentVariable("COMPANY_FLAG") === "bcp";
const IS_DEV = getEnvironmentVariable("IS_DEV") === "true";
const IS_GW = getEnvironmentVariable("COMPANY_FLAG") === "gw";

export default function AccountDetailsTab(props) {
  const authContext = useContext(AuthContext);

  // Language setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.account.accountDetails"
  });

  const { user, setUser, navigate } = props;
  const [processing, setProcessing] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const [isBetaUser, setIsBetaUser] = useState(
    authContext?.authState?.identity?.betaUser === "true"
  );
  const [showBetaSwitch, setShowBetaSwitch] = useState(
    authContext?.authState?.identity?.betaUser === "true" ||
      authContext?.authState?.identity?.allowedBeta === "true" ||
      authContext?.authState?.identity?.isAdmin
  );
  const cookieTypes = getCookieTypes();

  const setCookieConsent = useSetCookieConsent();

  const [customConsent, setCustomConsent] = useState({
    consented: true,
    essential: true,
    analytical: authContext?.authState?.cookieConsent?.analytical || false,
    performance: authContext?.authState?.cookieConsent?.performance || false,
    informational: authContext?.authState?.cookieConsent?.informational || false
  });
  const [leaderboardRedesign, setLeaderboardRedesign] = useState(
    getLocalStorage("leaderboardRedesign") === "true"
  );

  useEffect(() => {
    const check = async () => {
      try {
        const currentSession = await currentAuthSession();

        if (
          authContext?.authState?.identity?.betaUser === "true" ||
          authContext?.authState?.identity?.allowedBeta === "true" ||
          authContext?.authState?.identity?.isAdmin ||
          currentSession?.identity?.allowedBeta === "true"
        ) {
          setShowBetaSwitch(true);
        }
      } catch (err) {
        postNotification("error", err);
      }
    };

    check();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const consentObj = {
      consented: true,
      essential: true,
      analytical: customConsent.analytical,
      performance: customConsent.performance,
      informational: customConsent.informational
    };

    // If the user has not changed their consent, do not update the cookie
    const cookieConsent = getCookie("CookieConsent");
    if (cookieConsent) {
      let parsedConsent = {};

      try {
        parsedConsent = JSON.parse(cookieConsent);
      } catch {}

      if (areObjectsEqual(parsedConsent, consentObj)) {
        return;
      }
    }

    setCookieConsent(consentObj);

    // eslint-disable-next-line
  }, [customConsent]);

  return (
    <>
      <Formik
        initialValues={{
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          email: user?.email || "",
          locationId: user?.locationId || "",
          marketing:
            user?.sendNotifications === true
              ? "Yes"
              : user?.sendNotifications === false
                ? "No"
                : ""
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().max(128).required("Required"),
          lastName: Yup.string().max(128).required("Required"),
          email: Yup.string()
            .max(128)
            .email("Must be a valid email")
            .required("Required"),
          marketing: Yup.string().nullable().required("Required")
        })}
        onSubmit={async (values) => {
          try {
            setProcessing(true);
            var body = {
              ...values,
              username: values.email,
              sendNotifications: values.marketing?.toLowerCase() === "yes"
            };
            delete body.marketing;

            var apiUser = await postData(`/users/${user.id}`, {
              body: {
                set: body
              }
            });
            setUser({
              ...user,
              ...apiUser
            });

            await refreshTokens();

            postNotification("success", t("notifications.success.userUpdated"));
          } catch (err) {
            postNotification("error", err);
          }
          setProcessing(false);
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700">
                  {t("userSettings", {
                    defaultValue: "User Settings"
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Tooltip
                  title={t("tooltips.name", {
                    defaultValue:
                      "Changing your name from this screen will NOT update existing player records, only future player records."
                  })}
                >
                  <TextField
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    name="firstName"
                    value={values.firstName}
                    label={t("firstName", {
                      defaultValue: "First Name",
                      keyPrefix: "fields"
                    })}
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Tooltip
                  title={t("tooltips.name", {
                    defaultValue:
                      "Changing your name from this screen will NOT update existing player records, only future player records."
                  })}
                >
                  <TextField
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    name="lastName"
                    value={values.lastName}
                    label={t("lastName", {
                      defaultValue: "Last Name",
                      keyPrefix: "fields"
                    })}
                    variant="outlined"
                    required
                    fullWidth
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  name="email"
                  value={values.email}
                  label={t("email", {
                    defaultValue: "Email",
                    keyPrefix: "fields"
                  })}
                  variant="outlined"
                  required
                  fullWidth
                  onBlur={handleBlur}
                  onChange={(e) => {
                    const trimmedString = e.target.value.trim().toLowerCase();
                    setFieldValue("email", trimmedString);
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl required>
                  <FormLabel>{t("marketingEmailConsent")}</FormLabel>

                  <RadioGroup row>
                    <FormControlLabel
                      value={values.marketing}
                      control={
                        <Radio
                          name="marketing"
                          checked={values.marketing === "Yes"}
                          defaultChecked={false}
                          onClick={(e) => {
                            setFieldValue("marketing", "Yes");
                          }}
                        />
                      }
                      label="Yes"
                    />
                    <FormControlLabel
                      value={values.marketing}
                      control={
                        <Radio
                          name="marketing"
                          checked={values.marketing === "No"}
                          defaultChecked={false}
                          onClick={(e) => {
                            setFieldValue("marketing", "No");
                          }}
                        />
                      }
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
                {Boolean(touched.marketing && errors.marketing) && (
                  <FormHelperText error>{errors.marketing}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700">
                  {t("cookieSettings", {
                    defaultValue: "Cookie Settings"
                  })}
                </Typography>
              </Grid>

              {cookieTypes.map((cookieType) => (
                <Grid item xs={12} key={cookieType.name}>
                  <FormControlLabel
                    label={
                      <Typography className="MuiFormControlLabel-label">
                        {t(`${cookieType.id}.title`, {
                          defaultValue: cookieType.name,
                          keyPrefix: "misc.cookies"
                        })}
                        <HelpButton
                          title={t(`${cookieType.id}.description`, {
                            defaultValue: cookieType.description,
                            keyPrefix: "misc.cookies"
                          })}
                        />
                      </Typography>
                    }
                    labelPlacement="end"
                    control={
                      <Switch
                        checked={
                          cookieType?.id === "essential"
                            ? true
                            : customConsent[cookieType.id]
                        }
                        onChange={() => {
                          setCustomConsent((prev) => ({
                            ...prev,
                            [cookieType.id]: !prev[cookieType.id]
                          }));
                        }}
                        disabled={cookieType?.id === "essential" ? true : false}
                      />
                    }
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Divider sx={{ mt: 1, mb: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700">
                  {t("additionalSettings", {
                    defaultValue: "Additional Settings"
                  })}
                </Typography>
              </Grid>

              {IS_BCP && showBetaSwitch && (
                <Grid item xs={12}>
                  <FormControlLabel
                    label={
                      <Typography>
                        {t("betaFeatures", {
                          defaultValue: "Beta Features",
                          keyPrefix: "buttons"
                        })}
                        <HelpButton
                          title={t("tooltips.betaFeatures", {
                            defaultValue:
                              "Toggle experimental features on or off. Beta features will display a banner or alert on the screen to let you know you are currently using an experimental page/feature."
                          })}
                        />
                      </Typography>
                    }
                    disabled={processing}
                    control={
                      <Switch
                        checked={isBetaUser}
                        onChange={async (e) => {
                          setProcessing(true);
                          try {
                            const apiBeta = await postData(
                              `/users/${authContext?.authState?.identity?.userId}/setBeta`,
                              {
                                beta: Boolean(
                                  !(
                                    authContext?.authState?.identity
                                      ?.betaUser === "true"
                                  )
                                )
                              }
                            );

                            await refreshTokens();
                            if (apiBeta) setIsBetaUser(!isBetaUser);
                          } catch (err) {
                            postNotification("error", err);
                          } finally {
                            setProcessing(false);
                          }
                        }}
                      />
                    }
                    labelPlacement="end"
                  />
                </Grid>
              )}

              {(IS_GW ||
                IS_DEV ||
                getLocalStorage("leaderboardRedesign") === "true") &&
                getEnvironmentVariable("LEADERBOARDS") !== "true" && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Typography>
                          Leaderboards / Rankings Redesign
                          <HelpButton
                            title={`Enable the new leaderboards/rankings pages. This may require a page refresh to go into effect.`}
                          />
                        </Typography>
                      }
                      disabled={processing}
                      control={
                        <Switch
                          checked={leaderboardRedesign}
                          onChange={async (e) => {
                            setLocalStorage(
                              "leaderboardRedesign",
                              !leaderboardRedesign
                            );
                            setLeaderboardRedesign(!leaderboardRedesign);
                          }}
                        />
                      }
                      labelPlacement="end"
                    />
                  </Grid>
                )}

              <Grid item xs={12}>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={() => setChangePasswordOpen(true)}
                >
                  {t("changePassword", {
                    defaultValue: "Change Password",
                    keyPrefix: "buttons"
                  })}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  {t("saveChanges", {
                    defaultValue: "Save Changes",
                    keyPrefix: "buttons"
                  })}
                </Button>
              </Grid>

              <DeleteAccount
                navigate={navigate}
                userId={user?.id || ""}
                t={t}
              />
            </Grid>
          </form>
        )}
      </Formik>
      <Modal
        disableAutoFocus
        open={changePasswordOpen}
        onClose={() => {
          setChangePasswordOpen(false);
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setChangePasswordOpen(false);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h2">
                {t("changePassword", {
                  defaultValue: "Change Password"
                })}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              password: "",
              newPassword: ""
            }}
            validationSchema={Yup.object().shape({
              password: Yup.string().max(255).required("Required"),
              newPassword: Yup.string().min(8).max(255).required("Required")
            })}
            onSubmit={async (values) => {
              try {
                setProcessing(true);

                await postData(
                  `/users/${user.id}/changepassword`,
                  {
                    body: {
                      oldPassword: values.password,
                      password: values.newPassword
                    }
                  },
                  "API", // Default API
                  null, // No custom headers
                  null, // No custom url
                  false // Do not refresh tokens on this call
                );

                postNotification(
                  "success",
                  t("notifications.success.passwordUpdated")
                );
                setChangePasswordOpen(false);
              } catch (err) {
                postNotification("error", err);
              }
              setProcessing(false);
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      error={Boolean(touched.password && errors.password)}
                      helperText={touched.password && errors.password}
                      autoComplete="password"
                      name="password"
                      value={values.password}
                      type="password"
                      label={t("currentPassword", {
                        defaultValue: "Current Password",
                        keyPrefix: "fields"
                      })}
                      variant="outlined"
                      required
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      error={Boolean(touched.newPassword && errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      name="newPassword"
                      value={values.newPassword}
                      type="password"
                      label={t("newPassword", {
                        defaultValue: "New Password",
                        keyPrefix: "fields"
                      })}
                      variant="outlined"
                      required
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      onClick={() => setChangePasswordOpen(false)}
                      fullWidth
                      type="button"
                      color="error"
                      variant="outlined"
                    >
                      {t("cancel", {
                        defaultValue: "Cancel",
                        keyPrefix: "buttons"
                      })}
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      type="submit"
                      color="success"
                      variant="contained"
                    >
                      {t("update", {
                        defaultValue: "Update",
                        keyPrefix: "buttons"
                      })}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
      {Boolean(processing) && <ModalLoadingFull />}
    </>
  );
}
